import { logger } from "@/infrastructure/services/LoggerService";
/** Same subset of BCP-47 the backend validator accepts (`LanguageTag.Pattern` in OPINK Domain). */
const BCP47_PATTERN = /^[a-zA-Z]{2,3}(-[a-zA-Z0-9]{2,8})*$/;
const displayNamesCache = new Map();
function displayNames(uiLocale) {
    let names = displayNamesCache.get(uiLocale);
    if (!names) {
        names = new Intl.DisplayNames([uiLocale], {
            type: "language",
            languageDisplay: "standard",
            fallback: "none",
        });
        displayNamesCache.set(uiLocale, names);
    }
    return names;
}
function capitalize(value, uiLocale) {
    return value.charAt(0).toLocaleUpperCase(uiLocale) + value.slice(1);
}
/**
 * Canonicalizes a tag (`fr_fr` → `fr-FR`), accepting Keycloak's underscore form.
 * Returns `null` when the value is not a valid tag.
 */
export function canonicalizeLanguageTag(value) {
    const candidate = value?.trim().replace(/_/g, "-");
    if (!candidate || candidate.length > 20 || !BCP47_PATTERN.test(candidate))
        return null;
    try {
        return Intl.getCanonicalLocales(candidate)[0] ?? null;
    }
    catch {
        return null;
    }
}
/**
 * Builds the localized label of `tag` (already canonical — as returned by the API) in `uiLocale`.
 * A tag with no display name falls back to its code.
 */
export function languageLabel(tag, uiLocale) {
    let full;
    let primary;
    try {
        const names = displayNames(uiLocale);
        full = names.of(tag);
        primary = names.of(tag.split("-")[0]);
    }
    catch {
        // RangeError on a malformed tag: same fallback as an unknown one.
    }
    if (!full) {
        logger.warn("No display name for language tag", tag);
        return { code: tag, label: tag, shortLabel: tag, display: tag };
    }
    const label = capitalize(full, uiLocale);
    const shortLabel = primary ? capitalize(primary, uiLocale) : label;
    return { code: tag, label, shortLabel, display: `${label} · ${tag}` };
}
/** Deduplicated labels of `tags` sorted by their localized name. */
export function languageLabels(tags, uiLocale) {
    return [...new Set(tags)]
        .map((tag) => languageLabel(tag, uiLocale))
        .sort((a, b) => a.label.localeCompare(b.label, uiLocale) || a.code.localeCompare(b.code));
}
